import { Component, EventEmitter, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { Client, CognitoUserPool, Section } from '@mira-avant/mtlibs';
import { NavigationService } from '../../../../services/navigation.service';
import { CognitoService } from '../../../../services/cognito.service';
import { ClientService } from '../../../../services/client.service';
import { UserService } from '../../../../services/user.service';
import { LanguageService } from '../../../../services/language.service';
import { CognitoGroupSelectorComponent } from '../../../ui/admin/cognito-group-selector/cognito-group-selector.component';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { AdminService } from '../../../../services/admin.service';
import { ClientSelectorComponent } from '../../../ui/admin/client-selector/client-selector.component';
import { TextFieldComponent } from '../../../fields/standard/text-field/text-field.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FieldLabelComponent } from '../../../ui/forms/field-label/field-label.component';
import { SectionsSelectorComponent } from '../../../ui/admin/sections-selector/sections-selector.component';

@Component({
	selector: 'app-generate-uuid-users-form',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		CognitoGroupSelectorComponent,
		ClientSelectorComponent,
		TextFieldComponent,
		FieldLabelComponent,
		SectionsSelectorComponent,
	],
	templateUrl: './generate-uuid-users-form.component.html',
	styleUrl: './generate-uuid-users-form.component.scss',
})
export class GenerateUuidUsersFormComponent implements OnInit {
	@Output('onSubmit') public onSubmitEmitter: EventEmitter<any> = new EventEmitter<any>();
	private sub: Subscription | undefined;
	public loading: boolean = false;
	public groups: string[] = [];
	public selectedClient: Client | undefined;
	public selectedSections: Section[] = [];
	public selectedSectionUuids: string[] = [];
	public selectedGroups: string[] = [];
	public activeStartDate: Date | undefined;
	public activeEndDate: Date | undefined;
	public active: boolean = true;

	public form: FormGroup = this.fb.group({
		userCount: ['', TextFieldComponent.validators],
	});

	constructor(
		private fb: FormBuilder,
		public nav: NavigationService,
		public cognitoService: CognitoService,
		public clientService: ClientService,
		public userService: UserService,
		public languageService: LanguageService,
		public adminService: AdminService
	) {}

	ngOnInit() {
		this.sub = this.adminService.selectedUserPool$.subscribe((pool: CognitoUserPool | undefined) => {
			if (!pool) {
				return;
			}

			this.cognitoService.listUserGroups(pool.id).then((groups: string[]) => {
				this.groups = groups;
			});
		});
	}

	public onClientSelect(client: Client): void {
		this.selectedClient = client;
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.reset$();
	}

	private async reset$(): Promise<void> {
		this.selectedClient = undefined;
		this.selectedSections = [];
		this.selectedGroups = [];
	}

	public onSelectClient(client: Client) {
		this.selectedClient = client;
	}

	public onSectionsChanged(sections: Section[]): void {
		this.selectedSections = sections;
		this.selectedSectionUuids = sections.map((section: Section) => section.uuid);
	}

	public onGroupsChanged(groups: string[]): void {
		this.selectedGroups = groups;
	}

	public async onSubmit$() {
		try {
			this.loading = true;

			const userPoolId: string | undefined = this.adminService.selectedUserPool$.value?.id;

			if (!userPoolId) {
				throw new Error('No user pool selected');
			}

			if (!this.selectedClient) {
				throw new Error('No client selected');
			}

			// First create the bulk users
			const users: Array<{
				username: string;
				temporaryPassword: string;
				cognitoUuid: string;
				userUuid?: string;
				success: boolean;
			}> = await this.cognitoService.createBulkUuidUsers(
				userPoolId,
				this.selectedClient,
				this.selectedSections,
				this.selectedGroups,
				this.activeStartDate,
				this.activeEndDate,
				this.active,
				this.form.value.userCount
			);

			// Finally, download the CSV with the created users
			await this.cognitoService.downloadUsersAsCsv(users);

			// Notify parent component
			this.onSubmitEmitter.emit(users);
		} catch (error: any) {
			alert('Error creating and updating users: ' + error.message);
		} finally {
			this.loading = false;
		}
	}

	ngOnDestroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
	}
}
